import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListSkeleton } from "@/components/shared/Skeletons";
import { Wallet, TrendingUp, Download } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { Field } from "@/components/forms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useStudentTips,
  useWithdrawTips,
  useMyWithdrawals,
  useWallet,
  useWithdrawalQuote,
  DEMO_STUDENT_TIPS,
  type Withdrawal,
} from "@/hooks/useTips";
import { useAuthStore } from "@/store/authStore";
import { formatDate, formatTZS } from "@/utils/formatters";

export const Route = createFileRoute("/student/tips/")({ component: StudentTips });

// Smallest withdrawal the API accepts, in TZS.
const MIN_WITHDRAWAL = 1000;

const WITHDRAWAL_STATUS: Record<
  Withdrawal["status"],
  { label: string; variant: "warning" | "success" | "destructive" }
> = {
  processing: { label: "Sending", variant: "warning" },
  completed: { label: "Sent", variant: "success" },
  failed: { label: "Failed", variant: "destructive" },
  reversed: { label: "Reversed", variant: "destructive" },
};

function StudentTips() {
  const { user } = useAuthStore();
  const { data = [], isLoading } = useStudentTips();
  const { data: wallet } = useWallet();
  const { data: withdrawals = [] } = useMyWithdrawals();
  const withdrawTips = useWithdrawTips();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [quotedAmount, setQuotedAmount] = useState(0);
  const tips = user?.isDemo ? (DEMO_STUDENT_TIPS as any[]) : Array.isArray(data) ? data : [];
  // The API's balance already leaves out money being withdrawn or paid out.
  const balance = Number((wallet as { balance?: number } | null)?.balance ?? 0);

  // Ask for the fee once the student stops typing for a moment.
  useEffect(() => {
    const t = setTimeout(() => setQuotedAmount(Math.round(Number(amount)) || 0), 400);
    return () => clearTimeout(t);
  }, [amount]);
  const quote = useWithdrawalQuote(
    quotedAmount,
    open && !user?.isDemo && quotedAmount >= MIN_WITHDRAWAL && quotedAmount <= balance,
  );
  const quoteError = (quote.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;

  const openWithdraw = () => {
    setAmount(String(Math.floor(balance)));
    setOpen(true);
  };

  const submitWithdraw = async () => {
    const value = Math.round(Number(amount));
    if (!value || value < MIN_WITHDRAWAL) {
      toast.error(`The minimum withdrawal is ${formatTZS(MIN_WITHDRAWAL)}.`);
      return;
    }
    if (value > balance) {
      toast.error("That is more than your available balance.");
      return;
    }
    if (!phone.trim()) {
      toast.error("Enter the mobile money number to send to.");
      return;
    }
    try {
      const w = await withdrawTips.mutateAsync({ amount: value, phone: phone.trim() });
      setOpen(false);
      const received = w.payout_amount ?? value;
      if (w.status === "completed") {
        toast.success(`${formatTZS(received)} sent to your phone.`);
      } else {
        toast.success("Withdrawal on its way", {
          description: "We'll let you know as soon as it reaches your phone.",
        });
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error(data?.message || "Withdrawal failed", {
        description: data?.error || (err instanceof Error ? err.message : undefined),
      });
    }
  };

  if (isLoading)
    return <ListSkeleton />;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title="Tips"
        subtitle="Track mentor and partner rewards in one place."
        action={
          <Button
            disabled={balance < MIN_WITHDRAWAL || withdrawTips.isPending}
            onClick={openWithdraw}
          >
            <Download />
            Withdraw to mobile money
          </Button>
        }
      />

      <section
        aria-label="Balance"
        className="flex items-center justify-between gap-4 rounded-md border border-border bg-card p-4 md:p-5"
      >
        <div className="flex items-center gap-3">
          <span
            className="flex size-10 items-center justify-center rounded-sm border border-border bg-background text-body"
            aria-hidden
          >
            <Wallet className="size-5" />
          </span>
          <div>
            <p className="eyebrow">Available to withdraw</p>
            <p className="text-[28px] leading-9 font-normal tracking-[-0.8px] text-foreground tabular-nums">
              {formatTZS(balance)}
            </p>
          </div>
        </div>
        <TrendingUp className="size-5 text-muted-foreground" aria-hidden />
      </section>

      {tips.length > 0 && (
        <SectionCard title="Tips received" noPadding>
          <ul className="divide-y divide-border">
            {tips.map((tip, i) => {
              const id = String(tip.id || i);
              return (
                <li key={id} className="flex items-center justify-between gap-4 px-4 py-3 md:px-5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {tip.source || tip.partner || "Partner tip"}
                    </p>
                    <p className="text-caption text-muted-foreground">
                      {formatDate(tip.created_at || tip.date)}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="font-mono text-sm text-foreground tabular-nums">
                      {formatTZS(Number(tip.amount || 0))}
                    </span>
                    <Badge variant="success" dot>
                      Received
                    </Badge>
                  </div>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      )}

      {withdrawals.length > 0 && (
        <SectionCard title="Withdrawals" noPadding>
          <ul className="divide-y divide-border">
            {withdrawals.map((w) => {
              const st = WITHDRAWAL_STATUS[w.status] ?? WITHDRAWAL_STATUS.processing;
              return (
                <li key={w.id} className="flex items-center justify-between gap-4 px-4 py-3 md:px-5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">To {w.phone}</p>
                    <p className="text-caption text-muted-foreground">
                      {formatDate(w.created_at)}
                      {w.failure_reason ? ` · ${w.failure_reason}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="font-mono text-sm text-foreground tabular-nums">
                      {formatTZS(Number(w.payout_amount ?? w.amount ?? 0))}
                    </span>
                    {Number(w.fee) > 0 && (
                      <span className="text-caption text-muted-foreground">
                        {formatTZS(Number(w.amount))} less {formatTZS(Number(w.fee))} fee
                      </span>
                    )}
                    <Badge variant={st.variant} dot>
                      {st.label}
                    </Badge>
                  </div>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && !withdrawTips.isPending && setOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Withdraw tips</DialogTitle>
            <DialogDescription>
              The money is sent to your M-Pesa, Airtel Money, Mixx by Yas or HaloPesa wallet.
              Available: {formatTZS(balance)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Amount (TZS)" hint={`Minimum ${formatTZS(MIN_WITHDRAWAL)}`}>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min={MIN_WITHDRAWAL}
                max={balance}
                step={100}
              />
            </Field>
            <Field label="Mobile money number">
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0712 345 678"
                inputMode="tel"
                autoComplete="tel"
              />
            </Field>
            {quote.data && quotedAmount === Math.round(Number(amount)) && (
              <dl className="space-y-1 rounded-sm bg-muted p-3 text-[13px]">
                <div className="flex justify-between text-muted-foreground">
                  <dt>Snippe fee</dt>
                  <dd className="tabular-nums">− {formatTZS(quote.data.fee)}</dd>
                </div>
                <div className="flex justify-between text-sm font-medium text-foreground">
                  <dt>You receive</dt>
                  <dd className="tabular-nums">{formatTZS(quote.data.payout_amount)}</dd>
                </div>
              </dl>
            )}
            {quoteError && (
              <p className="text-[13px] text-destructive" role="alert">
                {quoteError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={withdrawTips.isPending}>
              Cancel
            </Button>
            <Button
              onClick={submitWithdraw}
              disabled={withdrawTips.isPending || (!user?.isDemo && !quote.data)}
            >
              {withdrawTips.isPending ? "Sending…" : "Withdraw"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
